import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { HelpCircle } from 'lucide-react';
import TransportPageLayout from '../../components/transportation/TransportPageLayout';
import { setSeoMetadata } from '../../lib/seo';
import { ROUTES } from '../../data/stjSite';

const FAQS = [
  {
    q: 'Does St John have Uber or Lyft?',
    a: 'No. Uber and Lyft do not operate on St John. Taxis are the main on-demand option for getting around.',
  },
  {
    q: 'Should I rent a vehicle in St John?',
    a: 'It isn’t strictly necessary. The roads are steep and scenic but often lack signs, guard rails and lighting, and you’ll be driving on the left — many visitors prefer to leave the driving to a taxi.',
  },
];

const IslandTaxiPage = () => {
  useEffect(() => {
    setSeoMetadata({
      title: 'Island Taxi Services St John | Getting Around | St John USVI',
      description:
        'Taxis are widely available on St John and a great way to get around. Rates are set, cash-only, and the taxi stand in Cruz Bay is right next to the ferry dock.',
      keywords: 'island taxi st john, taxi rates st john, st john taxi service, getting around st john',
      canonicalPath: '/island-taxi-st-john',
      ogType: 'website',
    });
  }, []);

  return (
    <TransportPageLayout title="Island Taxi Services" subtitle="St John, Virgin Islands" adId="island-taxi">
      <section className="rounded-2xl border border-slate-200 bg-white p-6 text-center shadow-sm">
        <h2 className="text-xl font-extrabold text-slate-900">Several taxi companies operate on St John</h2>
        <p className="mt-2 text-slate-700 leading-relaxed">
          Taxis are widely available and a good way to get around the island. If you’re nervous about driving on the
          left, or you just want to sit back and enjoy the view, a taxi is for you.
        </p>
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <h2 className="text-lg font-extrabold text-slate-900">Island Taxis</h2>
        <p className="mt-2 text-slate-700 leading-relaxed">
          If you’re staying on St John or just visiting for the day, taxi service is a great way to see the island.
          The taxi stand in Cruz Bay is located right next to the ferry dock — you can’t miss it.
        </p>
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <h2 className="text-lg font-extrabold text-slate-900">Taxi Rates</h2>
        <p className="mt-2 text-slate-700 leading-relaxed">
          Taxi rates are non-negotiable and cash only. Rates are posted inside each vehicle and are charged per person,
          so a single rider should expect to pay a little more than a shared fare. Please don’t forget to tip your
          driver.
        </p>
      </section>

      <section className="space-y-4" data-testid="island-taxi-faqs">
        {FAQS.map((f, i) => (
          <div key={i} className="rounded-2xl border border-slate-200 bg-slate-50 p-6">
            <h3 className="flex items-center gap-2 text-base font-bold text-slate-900">
              <HelpCircle className="h-5 w-5 text-red-600" /> {f.q}
            </h3>
            <p className="mt-2 text-slate-700 leading-relaxed">{f.a}</p>
          </div>
        ))}
      </section>

      <section className="rounded-2xl border border-red-200 bg-red-50 p-6 text-center">
        <h2 className="text-lg font-extrabold text-slate-900">Need a ride arranged for you?</h2>
        <p className="mt-2 text-slate-700">
          Tell our concierge team your dates and plans and we’ll help line up taxis, transfers and more — free of charge.
        </p>
        <Link
          to={ROUTES.contactUs}
          className="mt-4 inline-flex items-center justify-center rounded-full bg-red-600 px-6 py-3 text-sm font-bold uppercase tracking-wide text-white transition hover:bg-red-700"
          data-testid="island-taxi-contact-cta"
        >
          Contact Our Concierge
        </Link>
      </section>
    </TransportPageLayout>
  );
};

export default IslandTaxiPage;
